import { useState } from "react";
import type { User } from "@printtat/shared";
import { useForm } from "@tanstack/react-form";
import { FormField, Alert, Button } from "@printtat/ui";
import { apiFetch } from "@/lib/apiFetch";

interface AccountFormData {
  firstName: string;
  lastName: string;
  phoneNumber?: string;
  address?: string;
}

interface AccountFormProps {
  user: User;
  isEditing: boolean;
  onEditComplete: () => void;
}

export const AccountForm = ({
  user,
  isEditing,
  onEditComplete,
}: AccountFormProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const form = useForm({
    defaultValues: {
      firstName: user.firstName || "",
      lastName: user.lastName || "",
      phoneNumber: user.phoneNumber || "",
      address: user.address || "",
    },
    onSubmit: async ({ value }: { value: AccountFormData }) => {
      setIsSubmitting(true);
      setSubmitError(null);
      setSubmitSuccess(false);

      try {
        const response = await apiFetch("/auth/user", {
          method: "PUT",
          body: value,
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(
            errorData.message || "Failed to update account information"
          );
        }

        setSubmitSuccess(true);
        onEditComplete();

        // Refresh user data
        window.location.reload();
      } catch (error) {
        setSubmitError(
          error instanceof Error ? error.message : "An error occurred"
        );
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  if (!isEditing) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              First Name
            </label>
            <p className="mt-1 text-sm text-gray-900">
              {user.firstName || "Not provided"}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Last Name
            </label>
            <p className="mt-1 text-sm text-gray-900">
              {user.lastName || "Not provided"}
            </p>
          </div>
        </div>

        <div className="flex flex-row gap-6">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700">
              Email
            </label>
            <p className="mt-1 text-sm text-gray-900">{user.email}</p>
            <p className="mt-1 text-sm text-gray-500">
              Email cannot be changed from this page
            </p>
          </div>
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700">
              Phone Number
            </label>
            <p className="mt-1 text-sm text-gray-900">
              {user.phoneNumber || "Not provided"}
            </p>
          </div>
        </div>

        <div className="flex-1">
          <label className="block text-sm font-medium text-gray-700">
            Address
          </label>
          <p className="mt-1 text-sm text-gray-900">
            {user.address || "Not provided"}
          </p>
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        form.handleSubmit();
      }}
      className="space-y-6"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField
          form={form}
          name="firstName"
          label="First Name"
          type="text"
          placeholder="Enter your first name"
          required
        />

        <FormField
          form={form}
          name="lastName"
          label="Last Name"
          type="text"
          placeholder="Enter your last name"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">Email</label>
        <p className="mt-1 text-lg text-gray-900">{user.email}</p>
        <p className="mt-1 text-sm text-gray-500">
          Email cannot be changed from this page
        </p>
      </div>

      <FormField
        form={form}
        name="phoneNumber"
        label="Phone Number"
        type="tel"
        placeholder="Enter your phone number"
      />

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Address
        </label>
        <textarea
          className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm"
          placeholder="Enter your address"
          rows={3}
          value={form.getFieldValue("address") || ""}
          onChange={(e) => form.setFieldValue("address", e.target.value)}
        />
      </div>

      {submitError && <Alert variant="error">{submitError}</Alert>}
      {submitSuccess && <Alert variant="success">Account updated successfully!</Alert>}

      {/* Form Actions */}
      <div className="flex justify-end space-x-3">
        <Button
          type="button"
          variant="outline"
          onClick={onEditComplete}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={!form.state.canSubmit}
          loading={isSubmitting}
        >
          Save Changes
        </Button>
      </div>
    </form>
  );
};
